// Route: /voice-tutor/new — 3-шаговый мастер голосового репетитора.
// Step1 Source → Step2 Plan (длина + генерация) → Step3 Voice + Start.
// Реальный flow: lessonPlans.generate → sessions.start → relay → store → session.

import type { CourseAudienceAge, CourseProgramLevel } from "@studyhub/shared/contracts/courses";
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import {
	Button,
	cn,
	ElevatedPanel,
	NARROW_PAGE_SHELL,
	PANEL_PAD,
	SectionHeader,
} from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconArrowLeft, IconArrowRight, IconLoader2, IconWaveSine } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LengthSlider, VOICE_TUTOR_DURATION_MARKS } from "@/components/audio/length-slider";
import { pickRandomVoiceDefaults, type VoiceGenderFilter } from "@/components/audio/voice-grid";
import { WizardSourceStep } from "@/components/audio-recaps/wizard-source-step";
import { type WizardStep, WizardStepper } from "@/components/audio-recaps/wizard-stepper";
import { LevelAudiencePicker } from "@/components/shared/level-audience-picker";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { WizardStickyFooter } from "@/components/shared/wizard-sticky-footer";
import type { MaterialsScopeSelection } from "@/components/tutor/chat/materials-scope-dialog";
import { useLessonPlanReadiness } from "@/components/voice-tutor/use-lesson-plan-readiness";
import { DEFAULT_TUTOR_VOICE_ID, tutorVoiceLabel } from "@/components/voice-tutor/voices";
import {
	LessonPlanPreview,
	useVoiceSessionStart,
	WizardStepWrap,
} from "@/components/voice-tutor/wizard-helpers";
import { WizardVoiceStep } from "@/components/voice-tutor/wizard-voice-step";
import { type Step1SourceTab, useVoicesList } from "@/hooks/use-audio-recap-wizard";
import { useRequireOnline } from "@/hooks/use-require-online";
import { useCurrentUser, useSession } from "@/hooks/use-session";
import { normalizeLocale } from "@/i18n/locales";
import { trackGenerationAccepted } from "@/lib/ecosystem-analytics";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { api } from "@/lib/trpc-client";
import { useUpgradeModalStore } from "@/stores/upgrade-modal";
import { useVoiceSessionStore } from "@/stores/voice-session";

const searchSchema = z.object({
	demo: z.enum(["default", "loading", "no-quota", "free-tier"]).optional(),
	setId: z.string().uuid().optional(),
	materialId: z.string().uuid().optional(),
});

export const Route = createFileRoute("/voice-tutor/new")({
	validateSearch: tolerantSearch(searchSchema),
	component: VoiceTutorIdlePage,
	pendingComponent: PagePendingFallback,
});

const VOICE_WIZARD_STEPS = [
	{ step: 1 as WizardStep, labelKey: "wizard.step.source" },
	{ step: 2 as WizardStep, labelKey: "wizard.step.plan" },
	{ step: 3 as WizardStep, labelKey: "wizard.step.voice" },
] as const;

/** Gemini Live ожидает PascalCase id; VoiceGrid отдаёт lowercase. */
function toTutorVoiceId(id: string): string {
	if (!id) return DEFAULT_TUTOR_VOICE_ID;
	return id.charAt(0).toUpperCase() + id.slice(1);
}

function VoiceTutorIdlePage(): React.ReactElement {
	const { t, i18n } = useTranslation("voice");
	const { online, guard } = useRequireOnline();
	const navigate = useNavigate({ from: "/voice-tutor/new" });
	const search = useSearch({ from: "/voice-tutor/new" });
	const sessionUser = useCurrentUser();
	const { tier: storeTier } = useSession();
	const tier = sessionUser?.subscriptionTier ?? storeTier;

	const [step, setStep] = useState<WizardStep>(1);
	const [sourceTab, setSourceTab] = useState<Step1SourceTab>("existing");
	const [materialIds, setMaterialIds] = useState<string[]>(
		search.setId ? [] : search.materialId ? [search.materialId] : [],
	);
	const [setId, setSetId] = useState<string | null>(search.setId ?? null);
	const [language, setLanguage] = useState(() =>
		normalizeLocale(i18n.resolvedLanguage ?? i18n.language),
	);
	const [durationMin, setDurationMin] = useState(15);
	// Ступень НЕ предвыбрана: вопрос необязательный, и молчание значит «не
	// заявляли» — тогда план и разговор идут по общей планке, как до появления
	// шага. Предвыбранная «середина» тихо навязала бы уровень тем, кто его не
	// называл. Возраст спрашивается только под «Начальным» (как в брифе курса),
	// а `null` там — это ответ «взрослому», а не пропуск.
	const [level, setLevel] = useState<CourseProgramLevel | null>(null);
	const [audienceAge, setAudienceAge] = useState<CourseAudienceAge | null>(null);
	/** Возраст едет на сервер только со ступенью, под которой его спрашивали. */
	const effectiveAudienceAge = level === "beginner" ? audienceAge : null;
	const [voiceId, setVoiceId] = useState(DEFAULT_TUTOR_VOICE_ID);
	const [genderFilter, setGenderFilter] = useState<VoiceGenderFilter>("all");
	const [planId, setPlanId] = useState<string | null>(null);

	// Язык занятия НЕ входит в signature: смена языка на step 3 не сбрасывает план.
	// Подача входит в подпись: смена уровня или возраста обязана сбросить уже
	// сгенерированный план — он написан другим языком и другой глубины.
	const planSignature = `${materialIds.join(",")}|${setId ?? ""}|${durationMin}|${level ?? ""}|${effectiveAudienceAge ?? ""}`;
	const planSignatureRef = useRef<string | null>(null);
	const randomizedRef = useRef(false);

	// Поминутной квоты у Voice Tutor на сервере НЕТ: `voiceTutor.sessions.start`
	// — `quotaProcedure({})` (passthrough без kind), ключа
	// `voice_tutor_minutes_daily` нет в `QUOTA_KEYS`. Индикатор здесь читал
	// именно его и потому не рендерился никогда, а `quotaExhausted` был вечным
	// false. Подменять его `tts_minutes_daily`/`stt_minutes_daily` нельзя: эти
	// счётчики голосовая сессия не расходует, зато их исчерпание (озвучка
	// карточек, STT в чате) блокировало бы старт занятия. Доступ к фиче гейтит
	// тариф — `isLocked` ниже.
	// Вернуть индикатор, когда появятся ОБА: ключ в `QUOTA_KEYS` и реальное
	// списание в `sessions.start`.
	const quotaExhausted = false;
	const demoLocked = search.demo === "free-tier" || search.demo === "no-quota";
	const isLocked = tier === "free" || demoLocked;
	// Подписи замка не называют тариф, когда продажа скрыта: App Store 3.1.1 —
	// подписки не должно быть видно, пока её нельзя купить через IAP.
	// Сам гейт остаётся: доступ, который не продаём, не выдаём.
	const commerceVisible = useCommerceVisible();
	const lockedCta = commerceVisible ? t("idle.lockedCta") : t("idle.lockedCtaNoSale");
	const lockedHint = commerceVisible ? t("idle.lockedHint") : t("idle.lockedHintNoSale");

	const openUpgrade = useUpgradeModalStore((s) => s.open);
	const startMutation = api.voiceTutor.sessions.start.useMutation();
	const generatePlan = api.voiceTutor.lessonPlans.generate.useMutation({
		onSuccess: (result) => trackGenerationAccepted("lesson", result),
	});
	const setPending = useVoiceSessionStore((s) => s.setPending);
	const voicesQuery = useVoicesList({ language: language.slice(0, 2) });

	useEffect(() => {
		if (randomizedRef.current) return;
		const voices = voicesQuery.data?.voices;
		if (!voices?.length) return;
		randomizedRef.current = true;
		const pick = pickRandomVoiceDefaults(voices);
		if (!pick) return;
		setGenderFilter(pick.genderFilter);
		setVoiceId(toTutorVoiceId(pick.voiceId));
	}, [voicesQuery.data?.voices]);

	// Ожидание готовности плана — общий хук: опрос `getById` + событие сокета как
	// ускоритель. Одного `voice:lesson_plan:ready` мало (см. use-lesson-plan-readiness).
	const { plan, ready: planReady, failed: planFailed } = useLessonPlanReadiness(planId);

	useEffect(() => {
		if (!planId) return;
		if (planSignatureRef.current === null) return;
		if (planSignatureRef.current !== planSignature) {
			setPlanId(null);
			planSignatureRef.current = null;
		}
	}, [planId, planSignature]);

	const canGenerate = materialIds.length > 0 || !!setId;

	const handleScopeSave = (sel: MaterialsScopeSelection): void => {
		if (sel.collectionId) {
			setSetId(sel.collectionId);
			setMaterialIds([]);
		} else {
			setMaterialIds(sel.materialIds);
			setSetId(null);
		}
	};

	const handleGeneratePlan = async (): Promise<void> => {
		if (!canGenerate || !guard()) return;
		try {
			const res = await generatePlan.mutateAsync({
				language,
				...(setId
					? { setId }
					: materialIds.length > 1
						? { materialIds }
						: materialIds[0]
							? { materialId: materialIds[0] }
							: {}),
				durationTargetMin: durationMin,
				...(level ? { level } : {}),
				...(effectiveAudienceAge ? { audienceAge: effectiveAudienceAge } : {}),
			});
			setPlanId(res.plan.id);
			planSignatureRef.current = planSignature;
		} catch {
			toast.error(t("error.startFailed"));
		}
	};

	const voiceStart = useVoiceSessionStart({
		isLocked,
		quotaExhausted,
		planId,
		planReady,
		openUpgrade,
		startSession: (lessonPlanId) =>
			startMutation.mutateAsync({
				lessonPlanId,
				acceptedDisclaimer: true,
				saveTranscriptOptIn: true,
				language,
				voiceId: toTutorVoiceId(voiceId),
				speechSpeed: 1,
				durationTargetMin: durationMin,
			}),
		onStarted: (result) => {
			const resolvedVoice = toTutorVoiceId(voiceId);
			const sessionLang = result.session.language;
			setPending(result.session.id, {
				relay: result.relay,
				topic: result.session.topic,
				language: sessionLang,
				voiceId: resolvedVoice,
				voiceLabel: tutorVoiceLabel(resolvedVoice, sessionLang),
				speechSpeed: 1,
				durationTargetMin: durationMin,
				lessonPlanId: result.session.lessonPlanId,
				saveTranscriptOptIn: true,
			});
			void navigate({
				to: "/voice-tutor/session/$id",
				params: { id: result.session.id },
			});
		},
		onError: () => toast.error(t("error.startFailed")),
	});

	const goNext = () => setStep((s) => Math.min(3, s + 1) as WizardStep);
	const goPrev = () => setStep((s) => Math.max(1, s - 1) as WizardStep);

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
	}, [step]);

	return (
		<div className={cn(NARROW_PAGE_SHELL, "space-y-section pb-24")}>
			<div className="space-y-2">
				<Button
					asChild
					variant="ghost"
					size="sm"
					className="-ml-2 cursor-pointer text-muted-foreground"
				>
					<Link to="/voice-tutor">
						<IconArrowLeft size={14} className="mr-1" />
						{t("list.backToList")}
					</Link>
				</Button>
				<SectionHeader
					category="ai"
					icon={<IconWaveSine />}
					title={t("title")}
					description={t("idle.tagline")}
					stackActions
				/>
			</div>

			<WizardStepper
				current={step}
				onStepClick={setStep}
				steps={VOICE_WIZARD_STEPS}
				i18nNs="voice"
				ariaLabel={t("title")}
			/>

			<ElevatedPanel className={cn(PANEL_PAD, "space-y-5")}>
				{step === 1 && (
					<WizardStepWrap label={t("wizard.step.source")} hint={t("wizard.step1Hint")}>
						<WizardSourceStep
							sourceTab={sourceTab}
							materialIds={materialIds}
							setId={setId}
							onSourceTabChange={setSourceTab}
							onScopeChange={handleScopeSave}
						/>
					</WizardStepWrap>
				)}

				{step === 2 && (
					<WizardStepWrap label={t("wizard.step.plan")} hint={t("wizard.step2Hint")}>
						<div className="space-y-5">
							<div className="space-y-3">
								<div className="min-w-0 space-y-0.5">
									<h3 className="text-sm font-semibold text-foreground">
										{t("wizard.level.title")}
									</h3>
									<p className="text-xs leading-snug text-muted-foreground">
										{t("wizard.level.hint")}
									</p>
								</div>
								<LevelAudiencePicker
									value={level}
									onChange={setLevel}
									audienceAge={audienceAge}
									onAudienceAgeChange={setAudienceAge}
								/>
							</div>
							<LengthSlider
								value={durationMin}
								onChange={setDurationMin}
								unit="min"
								marksMin={VOICE_TUTOR_DURATION_MARKS}
							/>
							<LessonPlanPreview
								planId={planId}
								plan={plan}
								planReady={planReady}
								planFailed={planFailed}
								canGenerate={canGenerate}
								generating={generatePlan.isPending}
								onGenerate={() => void handleGeneratePlan()}
							/>
						</div>
					</WizardStepWrap>
				)}

				{step === 3 && (
					<WizardVoiceStep
						language={language}
						onLanguageChange={(lang) => setLanguage(normalizeLocale(lang))}
						voiceId={voiceId}
						onVoiceChange={(id) => setVoiceId(toTutorVoiceId(id))}
						genderFilter={genderFilter}
						onGenderFilterChange={setGenderFilter}
						planId={planId}
						planReady={planReady}
						planFailed={planFailed}
						quotaExhausted={quotaExhausted}
						onGeneratePlan={() => void handleGeneratePlan()}
					/>
				)}
			</ElevatedPanel>

			<WizardStickyFooter>
				<Button
					variant="outline"
					className="pointer-coarse:h-11 cursor-pointer"
					onClick={() => {
						if (step === 1) void navigate({ to: "/voice-tutor" });
						else goPrev();
					}}
				>
					<IconArrowLeft size={14} />
					{step === 1 ? t("wizard.cancel") : t("wizard.back")}
				</Button>
				{step < 3 ? (
					<Button
						onClick={goNext}
						disabled={step === 1 ? !canGenerate : !planReady}
						className="pointer-coarse:h-11 cursor-pointer"
					>
						{t("wizard.next")}
						<IconArrowRight size={14} />
					</Button>
				) : (
					<Button
						onClick={() => {
							if (!guard()) return;
							voiceStart.requestStart();
						}}
						disabled={startMutation.isPending || !planReady || !online}
						className="pointer-coarse:h-11 cursor-pointer"
					>
						{startMutation.isPending ? (
							<IconLoader2 size={16} className="animate-spin" />
						) : (
							<IconWaveSine size={16} />
						)}
						{isLocked ? lockedCta : t("actions.start")}
					</Button>
				)}
			</WizardStickyFooter>

			{isLocked && (
				<div className="sh-inset-field rounded-[var(--radius-md)] p-3 text-xs text-muted-foreground">
					{lockedHint}
				</div>
			)}
		</div>
	);
}
